/**
 * Staff Console → Orchestrator tab.
 *
 * Live view of the in-backend outreach engine (the n8n replacement): one row
 * per scheduler tick from `orchestrator_runs`, expandable into the per-action
 * phase trail from `orchestrator_events`. Auto-refreshes every 30 s.
 *
 * Reading the table: a healthy idle system shows a run every minute with
 * zeroes; a healthy busy one shows sent > 0 and blocked/failed ≈ 0. `mode`
 * distinguishes real ticks from dry runs; `error` is the tick-level failure
 * (per-action failures live in the expanded events).
 */

import { useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Loader2, RefreshCw, CheckCircle2, ShieldAlert, PauseCircle, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtLocale } from '@/lib/dates'
import {
  useOrchestratorRuns,
  useOrchestratorRunEvents,
  type OrchestratorRun,
} from './use-admin'

function outcomeBadgeClass(outcome: string): string {
  if (outcome === 'ok' || outcome === 'reused_draft' || outcome === 'already_connected') {
    return 'bg-[rgba(16,185,129,0.12)] text-[#34D399]'
  }
  if (outcome === 'budget_deferred' || outcome === 'skipped' || outcome === 'qg_regenerate') {
    return 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]'
  }
  return 'bg-[rgba(248,113,113,0.12)] text-[#F87171]'
}

function RunEvents({ runId }: { runId: string }) {
  const { data: events, isLoading } = useOrchestratorRunEvents(runId)
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-3 text-sm text-[var(--color-silver)]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading events…
      </div>
    )
  }
  if (!events || events.length === 0) {
    return (
      <p className="p-3 text-sm text-[var(--color-silver)]">
        No per-action events — the tick found nothing eligible to do.
      </p>
    )
  }
  return (
    <div className="space-y-1 p-2">
      {events.map((e) => (
        <div
          key={e.id}
          className="flex flex-wrap items-center gap-2 rounded-md bg-[rgba(255,255,255,0.03)] px-2.5 py-1.5 text-xs"
        >
          <span className="text-[var(--color-silver)] tabular-nums">
            {new Date(e.ts + 'Z').toLocaleTimeString()}
          </span>
          <Badge variant="outline" className="border-none bg-[rgba(59,130,246,0.12)] text-[#8FB4FA]">
            {e.phase}
          </Badge>
          <Badge variant="outline" className={cn('border-none', outcomeBadgeClass(e.outcome))}>
            {e.outcome}
          </Badge>
          {e.lead_name && (
            <span className="font-medium text-[var(--color-silver-bright)]">{e.lead_name}</span>
          )}
          {e.step_type && <span className="text-[var(--color-silver)]">{e.step_type}</span>}
          {e.campaign_name && (
            <span className="text-[var(--color-silver)]">· {e.campaign_name}</span>
          )}
          {typeof e.duration_ms === 'number' && (
            <span className="text-[var(--color-silver)] tabular-nums">{e.duration_ms}ms</span>
          )}
          {e.detail && (
            <span className="w-full truncate text-[var(--color-silver)]" title={e.detail}>
              {e.detail}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

function HourStat({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string
  value: number
  icon: LucideIcon
  tone: 'green' | 'amber' | 'red' | 'neutral'
}) {
  const toneClass =
    tone === 'green'
      ? 'text-[#34D399]'
      : tone === 'amber'
        ? 'text-[#FBBF24]'
        : tone === 'red'
          ? 'text-[#F87171]'
          : 'text-[var(--color-silver-bright)]'
  return (
    <Card className="flex-1">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-[var(--color-silver)]">{label}</p>
          <Icon className={cn('h-4 w-4', toneClass)} />
        </div>
        <div className={cn('mt-1 text-2xl font-bold tracking-tight', toneClass)}>
          {value.toLocaleString()}
        </div>
      </CardContent>
    </Card>
  )
}

export function OrchestratorPage() {
  const { data, isLoading, refetch, isRefetching } = useOrchestratorRuns()
  const [expanded, setExpanded] = useState<string | null>(null)

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center p-12 text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading orchestrator runs…
      </div>
    )
  }

  const hour = data.last_hour
  const interesting = (r: OrchestratorRun) =>
    r.considered > 0 || r.failed > 0 || r.error !== null || r.mode !== 'internal'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <HourStat label="Runs / hour" value={hour?.runs ?? 0} icon={RefreshCw} tone="neutral" />
        <HourStat label="Sent / hour" value={hour?.sent ?? 0} icon={CheckCircle2} tone="green" />
        <HourStat label="Blocked" value={hour?.blocked ?? 0} icon={ShieldAlert} tone="amber" />
        <HourStat label="Deferred" value={hour?.deferred ?? 0} icon={PauseCircle} tone="amber" />
        <HourStat label="Failed" value={hour?.failed ?? 0} icon={XCircle} tone="red" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base text-[var(--color-silver-bright)]">
            Recent ticks
            <span className="ml-2 text-xs font-normal text-[var(--color-silver)]">
              (click a row with activity to see its per-lead trail · auto-refreshes 30s)
            </span>
          </CardTitle>
          <button
            onClick={() => void refetch()}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.05)]"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
            Refresh
          </button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead className="text-right">Considered</TableHead>
                <TableHead className="text-right">Emitted</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Blocked</TableHead>
                <TableHead className="text-right">Deferred</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">ms</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="py-6 text-center text-sm text-[var(--color-silver)]">
                    No runs recorded yet. The engine writes one row per minute once
                    `orchestrator:mode` is set to `internal`.
                  </TableCell>
                </TableRow>
              )}
              {data.runs.map((r) => (
                <>
                  <TableRow
                    key={r.id}
                    onClick={() => interesting(r) && setExpanded(expanded === r.id ? null : r.id)}
                    className={cn(
                      interesting(r) && 'cursor-pointer hover:bg-[rgba(255,255,255,0.03)]',
                      r.failed > 0 || r.error ? 'bg-[rgba(248,113,113,0.05)]' : undefined,
                    )}
                  >
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">
                      {fmtLocale(r.started_at)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'border-none text-xs',
                          r.mode === 'internal'
                            ? 'bg-[rgba(16,185,129,0.12)] text-[#34D399]'
                            : 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]',
                        )}
                      >
                        {r.mode}
                        {r.trigger === 'manual' ? ' · manual' : ''}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.considered}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.emitted}</TableCell>
                    <TableCell className={cn('text-right tabular-nums', r.sent > 0 && 'font-semibold text-[#34D399]')}>
                      {r.sent}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', r.blocked > 0 && 'text-[#FBBF24]')}>
                      {r.blocked}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', r.deferred > 0 && 'text-[#FBBF24]')}>
                      {r.deferred}
                    </TableCell>
                    <TableCell className={cn('text-right tabular-nums', r.failed > 0 && 'font-semibold text-[#F87171]')}>
                      {r.failed}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-[var(--color-silver)]">
                      {r.duration_ms ?? ''}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-[#F87171]" title={r.error ?? ''}>
                      {r.error ?? ''}
                    </TableCell>
                  </TableRow>
                  {expanded === r.id && (
                    <TableRow key={`${r.id}-events`}>
                      <TableCell colSpan={10} className="bg-[rgba(255,255,255,0.02)] p-0">
                        <RunEvents runId={r.id} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
